/**
 * La synchronisation avec le serveur.
 *
 * ADR-001 : le client fait autorité pendant la séance, le serveur est un
 * **point de convergence**. Rien ici n'est sur le chemin critique — une partie
 * se joue entièrement hors ligne, et la synchronisation n'est qu'un rendez-vous
 * qu'on prend quand le réseau revient.
 *
 * # Ce que `revision` veut dire
 *
 * C'est **le numéro que le serveur a confirmé en dernier**, pas un compteur de
 * modifications locales. Modifier une fiche ne l'avance pas ; seul un dépôt
 * accepté le fait, et c'est le serveur qui rend le nouveau. Un conflit signifie
 * donc exactement une chose : *un autre appareil a déposé depuis mon dernier
 * rendez-vous*. C'est le seul cas qui mérite un arbitrage.
 *
 * # Ce qui n'est pas fait
 *
 * ADR-001 reporte volontairement l'arbitrage de deux appareils modifiant la
 * même fiche hors ligne. On ne tranche donc pas : on **rapporte** le conflit et
 * on laisse les deux versions intactes. Écraser l'une des deux perdrait une
 * séance de jeu, et personne ne saurait laquelle.
 */

import { base } from './base'
import type { FicheEnBase } from './personnages'
import type { PersonnageEnregistre } from '../rules'

/** Où joindre le serveur. Vide : la synchronisation est éteinte. */
const RACINE = import.meta.env.VITE_LOTC_API ?? ''

/** Ce qu'une liste du serveur rend, sans les fiches entières. */
interface Entree {
  id: string
  nom: string
  modifie_le: string
  revision: number
}

interface FicheDistante {
  id: string
  nom: string
  modifie_le: string
  revision: number
  personnage: PersonnageEnregistre
}

/** Une fiche que deux appareils ont fait diverger. */
export interface Conflit {
  id: string
  nom: string
  /** La révision que le serveur détient. */
  revision_serveur: number
  /** Celle que nous croyions à jour. */
  revision_locale: number
}

/** Ce qu'un rendez-vous a donné. */
export interface Bilan {
  envoyees: number
  recues: number
  conflits: Conflit[]
  /** Renseigné quand le rendez-vous n'a pas pu avoir lieu du tout. */
  panne: string | null
}

export function synchronisationConfiguree(): boolean {
  return RACINE.length > 0
}

const personnages = () => base.table<FicheEnBase, string>('personnages')

/**
 * `credentials: 'include'` n'est pas un détail : Authelia pose un cookie de
 * session sur le domaine, et sans lui chaque appel repartirait vers le portail.
 */
async function appeler(chemin: string, options: RequestInit = {}): Promise<Response> {
  return fetch(`${RACINE}${chemin}`, {
    ...options,
    credentials: 'include',
    headers: {
      'content-type': 'application/json',
      // Annoncer qu'on attend du JSON n'est pas une politesse : c'est ce qui
      // decide Authelia a repondre un code d'erreur plutot qu'a rediriger vers
      // son portail. Voir `versLePortail`.
      accept: 'application/json',
      ...(options.headers ?? {}),
    },
  })
}

/**
 * L'appel s'est-il heurte au portail d'authentification ?
 *
 * Authelia a deux facons de dire « connecte-toi d'abord », selon ce qu'il croit
 * lire : un code `401`/`403` quand il reconnait un appel de programme, une
 * redirection vers le portail quand il croit a une navigation. Les deux disent
 * la meme chose, mais la seconde est traitresse — `fetch` suit la redirection
 * en silence et rend la page du portail avec un code 200. On lirait du HTML
 * comme du JSON, pour conclure a une panne reseau qui n'existe pas.
 */
function versLePortail(reponse: Response): boolean {
  return reponse.status === 401 || reponse.status === 403 || reponse.redirected
}

/** Lit le corps en JSON, ou rend `null` — jamais d'exception. */
async function corps<T>(reponse: Response): Promise<T | null> {
  try {
    return (await reponse.json()) as T
  } catch {
    return null
  }
}

/**
 * Prend le rendez-vous : on descend ce que le serveur a de neuf, puis on
 * remonte ce qu'il n'a pas.
 *
 * Dans cet ordre, et non l'inverse : descendre d'abord évite de proposer une
 * écriture qu'on sait déjà périmée, et transforme un conflit potentiel en
 * simple mise à jour quand la fiche n'a pas bougé de notre côté.
 */
export async function synchroniser(): Promise<Bilan> {
  const bilan: Bilan = { envoyees: 0, recues: 0, conflits: [], panne: null }
  if (!synchronisationConfiguree()) {
    bilan.panne = "aucune adresse de serveur n'est configurée"
    return bilan
  }

  let distantes: Entree[]
  try {
    const reponse = await appeler('/personnages')
    if (versLePortail(reponse)) {
      bilan.panne = 'il faut d’abord se connecter au portail'
      return bilan
    }
    if (!reponse.ok) {
      bilan.panne = `le serveur a répondu ${reponse.status}`
      return bilan
    }
    const liste = await corps<Entree[]>(reponse)
    if (!liste) {
      bilan.panne = "le serveur n'a pas répondu ce qu'on attendait"
      return bilan
    }
    distantes = liste
  } catch {
    // `fetch` ne jette pas sur un code HTTP : il jette quand la requête n'a pas
    // pu aboutir du tout. Deux causes, et il vaut mieux les distinguer que de
    // dire « injoignable » — on ne saurait pas laquelle chercher.
    //
    // Soit il n'y a pas de réseau, et ce n'est pas une panne mais le cas
    // nominal du jeu. Soit l'adresse visée est d'une autre origine que
    // l'application, et le navigateur a bloqué l'appel avant même de partir.
    // Nommer l'adresse essayée suffit à trancher.
    bilan.panne = `${RACINE} est injoignable`
    return bilan
  }

  const locales = await personnages().toArray()
  const parId = new Map(locales.map((f) => [f.id, f]))

  // 1. Descendre. Une fiche que nous n'avons pas, ou dont le serveur détient une
  //    révision plus récente que celle qu'il nous avait confirmée.
  for (const entree of distantes) {
    const locale = parId.get(entree.id)
    if (locale && locale.revision >= entree.revision) continue

    // Le serveur est passé devant, ET nous avons touché à la fiche depuis notre
    // dernier dépôt : les deux ont divergé. On ne descend pas — écraser ici
    // détruirait le travail local sans que personne ne s'en aperçoive. Le
    // deuxième temps le signalera comme conflit.
    if (locale?.a_pousser) continue

    const recue = await lireDistante(entree.id)
    if (!recue) continue
    await personnages().put({
      ...recue,
      cree_le: locale?.cree_le ?? recue.modifie_le,
      a_pousser: false,
    })
    bilan.recues += 1
  }

  // 2. Remonter. Seulement ce qui a bougé : le serveur tranchera sur la
  //    révision, c'est lui qui détient la vérité de ce point-là.
  for (const locale of await personnages().toArray()) {
    if (locale.a_pousser === false) continue
    const issue = await deposer(locale)
    if (issue === 'envoyee') bilan.envoyees += 1
    else if (issue !== 'inchangee') bilan.conflits.push(issue)
  }

  return bilan
}

async function lireDistante(id: string): Promise<FicheDistante | null> {
  const reponse = await appeler(`/personnages/${encodeURIComponent(id)}`)
  if (versLePortail(reponse) || !reponse.ok) return null
  return corps<FicheDistante>(reponse)
}

/**
 * Dépose une fiche. Rend `envoyee`, `inchangee`, ou le conflit à arbitrer.
 *
 * Sur un dépôt accepté, le serveur rend la révision suivante : c'est elle qu'on
 * garde, et c'est ce qui fait que la prochaine écriture ne criera pas au
 * conflit.
 */
async function deposer(fiche: FicheEnBase): Promise<'envoyee' | 'inchangee' | Conflit> {
  let reponse: Response
  try {
    reponse = await appeler(`/personnages/${encodeURIComponent(fiche.id)}`, {
      method: 'PUT',
      body: JSON.stringify({
        id: fiche.id,
        nom: fiche.nom,
        modifie_le: fiche.modifie_le,
        revision: fiche.revision,
        personnage: fiche.personnage,
      }),
    })
  } catch {
    return 'inchangee'
  }

  if (reponse.status === 409) {
    const conflit = await corps<{ revision_serveur: number }>(reponse)
    if (!conflit) return 'inchangee'
    return {
      id: fiche.id,
      nom: fiche.nom,
      revision_serveur: conflit.revision_serveur,
      revision_locale: fiche.revision,
    }
  }
  if (versLePortail(reponse) || !reponse.ok) return 'inchangee'

  const confirmee = await corps<FicheDistante>(reponse)
  if (!confirmee) return 'inchangee'
  // Déposée : le serveur détient désormais notre version, et rend son numéro.
  await personnages().put({ ...fiche, revision: confirmee.revision, a_pousser: false })
  return 'envoyee'
}

/**
 * Tranche un conflit en faveur du serveur : on écrase la version locale.
 *
 * Le geste est explicite et destructeur, donc il ne s'exécute jamais tout seul.
 * L'inverse — imposer la version locale — se fait en synchronisant après avoir
 * adopté la révision du serveur.
 */
export async function adopterLaVersionDuServeur(id: string): Promise<boolean> {
  const recue = await lireDistante(id)
  if (!recue) return false
  const locale = await personnages().get(id)
  await personnages().put({
    ...recue,
    cree_le: locale?.cree_le ?? recue.modifie_le,
    // On adopte la sienne : il n'y a plus rien à lui envoyer.
    a_pousser: false,
  })
  return true
}

/**
 * Impose la version locale : on adopte le numéro du serveur sans prendre son
 * contenu, ce qui rend le prochain dépôt acceptable.
 */
export async function imposerLaVersionLocale(id: string, revisionServeur: number): Promise<void> {
  const locale = await personnages().get(id)
  if (!locale) return
  // On prend son numéro sans son contenu : le prochain dépôt sera accepté, et
  // c'est notre version qui l'emportera.
  await personnages().put({ ...locale, revision: revisionServeur, a_pousser: true })
}
